import { InternalPhoneNumberAdminViewDto } from '@act/shared/data-transfer-objects';
import {
  createAction,
  createEntityAdapter,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import { createApiThunk } from 'redux/utils/apiThunk';
import { useAppSelector } from 'redux/storeHooks';
import { InternalPhoneNumberRequests } from 'redux/requests/internal-phone-numbers.requests';
import { InternalPhoneNumberState } from 'redux/types/internalPhoneNumberTypes';

// THUNKS/ACTIONS ==============================================================

export const createInternalPhoneNumber = createApiThunk(
  InternalPhoneNumberRequests.createInternalPhoneNumber
);
export const updateInternalPhoneNumber = createApiThunk(
  InternalPhoneNumberRequests.updateInternalPhoneNumber
);
export const getAllInternalNumbers = createApiThunk(
  InternalPhoneNumberRequests.getAllInternalNumbers
);

export const createPhoneNumberSuccess =
  createAction<InternalPhoneNumberAdminViewDto>(
    'internalPhoneNumber/createPhoneNumberSuccess'
  );
export const updatePhoneNumberSuccess =
  createAction<InternalPhoneNumberAdminViewDto>(
    'internalPhoneNumber/updatePhoneNumberSuccess'
  );
// INITIAL STATE ===============================================================
const internalPhoneNumberEntityAdapter =
  createEntityAdapter<InternalPhoneNumberAdminViewDto>({
    sortComparer: (a, b) => {
      if (a.description < b.description) {
        return -1;
      }
      if (a.description > b.description) {
        return 1;
      }
      return 0;
    },
  });

const initialState: InternalPhoneNumberState = {
  internalPhoneNumbers: internalPhoneNumberEntityAdapter.getInitialState(),
  status: 'initial',
};

// SLICE =======================================================================
export const internalPhoneNumberSlice = createSlice({
  name: 'internalPhoneNumbers',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getAllInternalNumbers.pending, state => {
        state.status = 'loading';
        internalPhoneNumberEntityAdapter.removeAll(state.internalPhoneNumbers);
      })
      .addCase(
        getAllInternalNumbers.fulfilled,
        (state, action: PayloadAction<InternalPhoneNumberAdminViewDto[]>) => {
          state.status = 'success';
          internalPhoneNumberEntityAdapter.setAll(
            state.internalPhoneNumbers,
            action.payload
          );
        }
      )
      .addCase(getAllInternalNumbers.rejected, state => {
        state.status = 'failed';
      })
      .addCase(
        updatePhoneNumberSuccess,
        (state, action: PayloadAction<InternalPhoneNumberAdminViewDto>) => {
          internalPhoneNumberEntityAdapter.upsertOne(
            state.internalPhoneNumbers,
            action.payload
          );
          state.status = 'success';
        }
      )
      .addCase(createPhoneNumberSuccess, (state, action) => {
        internalPhoneNumberEntityAdapter.upsertOne(
          state.internalPhoneNumbers,
          action.payload
        );
        state.status = 'success';
      });
  },
});

// REDUCER =====================================================================
export const internalPhoneNumberReducer = internalPhoneNumberSlice.reducer;

// SELECTORS ===================================================================

const selectInternalPhoneNumbersSliceState = (state: {
  internalPhoneNumbers: typeof initialState;
}) => state.internalPhoneNumbers;

export const selectAllInternalNumbers = createSelector(
  selectInternalPhoneNumbersSliceState,
  s => s.internalPhoneNumbers
);

export const selectAllDefaultInternalNumbers = createSelector(
  selectInternalPhoneNumbersSliceState,
  s => {
    return Object.values(s.internalPhoneNumbers.entities).filter(
      number => number.isDefault === true
    );
  }
);
export const selectAllFeedbackInternalNumbers = createSelector(
  selectInternalPhoneNumbersSliceState,
  s => {
    return Object.values(s.internalPhoneNumbers.entities).filter(
      number => number.isFeedbackNumber === true
    );
  }
);

export const internalPhoneNumbersSelectors = {
  useSelectAllInternalNumbers: () => useAppSelector(selectAllInternalNumbers),
  useSelectAllDefaultInternalNumbers: () =>
    useAppSelector(selectAllDefaultInternalNumbers),
  useSelectAllFeedbackInternalNumbers: () =>
    useAppSelector(selectAllFeedbackInternalNumbers),
};
